import React from 'react';
import { Col, Row } from 'antd';
import { ExtensionDetailItem } from 'works/components/extension-offer/extension-detail-item';
import { pluralizeWeeksValue } from 'works/legacy/services/length-in-weeks-service';
import { toUSFormat } from 'works/legacy/lib/date-helper';
import { OfferChangeRequestType } from '@applicant-flow/graphql';
import { getExtensionOfferReasonDescription } from '@working-experience/services';
import { Divider } from '@common/components';

export function ExtensionOfferDetails({
  state,
  startDate,
  endDate,
  shiftType,
  originalShiftType,
  timeoffDescription,
  extensionReason,
  lengthInWeeks,
  originalEndDate,
  originalHoursPerShift,
  originalShiftsPerWeek,
  hoursPerShift,
  shiftsPerWeek,
  offerChangeRequests,
  overtimeThreshold,
}: {
  state: string;
  startDate: string;
  endDate: string;
  shiftType: string;
  originalShiftType: string;
  timeoffDescription: string;
  extensionReason: string;
  lengthInWeeks: number;
  originalEndDate: string;
  originalHoursPerShift: number;
  originalShiftsPerWeek: number;
  hoursPerShift: number;
  shiftsPerWeek: number;
  offerChangeRequests: OfferChangeRequestType[];
  overtimeThreshold?: number;
}) {
  const offerChangeRequest =
    offerChangeRequests?.length > 0
      ? offerChangeRequests[offerChangeRequests.length - 1]
      : null;

  const detail =
    offerChangeRequest?.offerChangeResponse?.detail ||
    ({} as {
      oldEndDate?: string;
      oldShiftType?: string;
      oldShiftsPerWeek?: number;
      oldHoursPerShift?: number;
      oldTimeoffDescription?: string;
    });

  const {
    oldEndDate,
    oldShiftType,
    oldShiftsPerWeek,
    oldHoursPerShift,
    oldTimeoffDescription,
  } = detail;

  const oldFormattedEndDate = oldEndDate ? toUSFormat(oldEndDate) : null;

  return (
    <div className="extension-offer-details">
      <Row>
        <Col span={8}>
          <ExtensionDetailItem title="Extension Start Date">
            <ExtensionDetailItem.Value value={toUSFormat(startDate)} />
          </ExtensionDetailItem>
        </Col>
        <Col span={8}>
          <ExtensionDetailItem title="Extended End Date">
            <ExtensionDetailItem.Value
              value={toUSFormat(endDate)}
              state={state}
              originalValue={toUSFormat(originalEndDate)}
              oldValue={oldFormattedEndDate}
            />
          </ExtensionDetailItem>
        </Col>
        <Col span={8}>
          <ExtensionDetailItem title="Assignment Length">
            <ExtensionDetailItem.Value
              value={`+${pluralizeWeeksValue(lengthInWeeks)}`}
              state={state}
            />
          </ExtensionDetailItem>
        </Col>
      </Row>
      <Divider gutter={2} />
      <Row>
        <Col span={24}>
          <ExtensionDetailItem title="Time Off">
            <ExtensionDetailItem.Value
              value={timeoffDescription}
              oldValue={oldTimeoffDescription}
              state={state}
            />
          </ExtensionDetailItem>
        </Col>
      </Row>
      <Divider gutter={2} />
      <Row>
        <Col span={8}>
          <ExtensionDetailItem title="Shift Type">
            <ExtensionDetailItem.Value
              value={shiftType}
              state={state}
              originalValue={originalShiftType}
              oldValue={oldShiftType}
            />
          </ExtensionDetailItem>
        </Col>
        <Col span={8}>
          <ExtensionDetailItem title="Shifts Per Week">
            <ExtensionDetailItem.Value
              value={shiftsPerWeek}
              state={state}
              originalValue={originalShiftsPerWeek}
              oldValue={oldShiftsPerWeek}
            />
          </ExtensionDetailItem>
        </Col>
        <Col span={8}>
          <ExtensionDetailItem title="Hours Per Shift">
            <ExtensionDetailItem.Value
              value={hoursPerShift}
              state={state}
              originalValue={originalHoursPerShift}
              oldValue={oldHoursPerShift}
            />
          </ExtensionDetailItem>
        </Col>
      </Row>
      {!!overtimeThreshold && (
        <>
          <Divider gutter={2} />
          <Row>
            <Col>
              <ExtensionDetailItem title="Overtime Threshold">
                <ExtensionDetailItem.Value
                  value={`${overtimeThreshold} hours`}
                />
              </ExtensionDetailItem>
            </Col>
          </Row>
        </>
      )}
      <Divider gutter={2} />
      <Row>
        <Col span={24}>
          <ExtensionDetailItem title="Reason">
            <ExtensionDetailItem.Value
              value={getExtensionOfferReasonDescription(extensionReason)}
            />
          </ExtensionDetailItem>
        </Col>
      </Row>
    </div>
  );
}
